import { useMemo } from "react";
import { useParams } from "react-router-dom";

import { Exception } from "@tsp-core/exception";
import { checkInArray } from "@tsp-core/utils/array";

import { SoulEntityType } from "../../enums";
import { LoadSoulEntityInfoPayload } from "../../types";
import { parseSoulEntityContentType, parseSoulEntityContentUuid, parseSoulEntityOrderUuid } from "../../utils";

import { SoulEntityContentCtx, SoulEntityOrderCtx } from "./soul-entity-context.types";

export function useSoulEntityContextTypeFromURI(): SoulEntityType {
  const { type } = useParams<Record<string, string>>();
  if (!type) {
    throw new Exception(-1, "Can not get SoulEntityType from URI", null);
  }

  if (!checkInArray(type, [SoulEntityType.CONTENT, SoulEntityType.ORDER])) {
    throw new Exception(-1, "Unknown SoulEntityType from URI", null);
  }

  return type;
}

export function useSoulEntityContextParamsFromURI(): SoulEntityContentCtx["params"] | SoulEntityOrderCtx["params"] {
  const type = useSoulEntityContextTypeFromURI();
  const params = useParams<Record<string, string>>();

  return useMemo(() => {
    const contentUuid = parseSoulEntityContentUuid(params.contentUuid);
    const contentType = parseSoulEntityContentType(params.contentType);
    const orderUuid = parseSoulEntityOrderUuid(params.orderUuid);

    if (type === SoulEntityType.CONTENT) {
      if (!contentUuid || !contentType) {
        throw new Exception(-1, "Unexpected SoulEntityContext from URI", null);
      }

      return {
        contentUuid,
        contentType,
        orderUuid: null,
      };
    }

    if (!orderUuid) {
      throw new Exception(-1, "Unexpected SoulEntityContext from URI", null);
    }

    return {
      contentUuid: null,
      contentType: null,
      orderUuid,
    };
  }, [type, params]);
}

export function useLoadSoulEntityPayload(): LoadSoulEntityInfoPayload {
  const type = useSoulEntityContextTypeFromURI();
  const params = useSoulEntityContextParamsFromURI();

  return useMemo((): LoadSoulEntityInfoPayload => {
    return { type, ...params } as LoadSoulEntityInfoPayload;
  }, [type, params]);
}
